// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this
// file, exactly as MovementTrendChart.tsx and StageTrendChart.tsx do for the same reason.
import React from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { monthLabel } from '../../../hooks/pipelineTracker/targetsContract';
import { money } from './pipelineParts';
import { CHART_AXIS_TICK, TOOLTIP_CARD } from './trackerKit';

/**
 * Pipeline velocity, one point per month: the business and one line per broker.
 *
 * Same chart language as the Deal movements 12-month trend (MovementTrendChart) and the Trends tab
 * (StageTrendChart), so the three trend charts read as one family (founder, 5 Oct 2026: "why isn't
 * the graph the same style as trend ones"): monotone lines with a dot per month, dashed #344155
 * grid at 0.3 opacity, small #8892b0 axes with no tick marks, "Oct 25" month labels, the dark
 * tooltip card and recharts' own legend.
 *
 * Values stay in integer cents per day end to end; money() formats them. A null month (nothing to
 * divide) is a gap in that line, never a drop to zero.
 */
export interface VelocityTrendSeries {
  key: string;
  name: string;
  colour: string;
  values: ReadonlyArray<number | null>;
}

/** '2026-10' to 'Oct 26', the label MovementTrendSection uses. */
export const shortMonth = (key: string): string => {
  const label = monthLabel(key); // 'Oct 2026'
  return label.length === 8 ? `${label.slice(0, 3)} ${label.slice(6)}` : label;
};

/** The chart's rows: one per month, one field per series. Exported for the tests. */
export function velocityTrendRows(
  months: readonly string[],
  series: readonly VelocityTrendSeries[],
): Array<Record<string, string | number | null>> {
  return months.map((m, i) => {
    const row: Record<string, string | number | null> = { label: shortMonth(m) };
    for (const s of series) row[s.key] = s.values[i] ?? null;
    return row;
  });
}

const perDay = (cents) => (cents === null || cents === undefined ? '-' : `${money(cents)}/day`);

const VelocityTrendChart = ({ months, series }) => {
  const data = velocityTrendRows(months, series);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    return (
      <div className={TOOLTIP_CARD}>
        <p className="mb-1 font-bold">{label}</p>
        {series.map((s) => (
          <p key={s.key} style={{ color: s.colour }}>
            {s.name}: <span className="tabular-nums">{perDay(row[s.key])}</span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <XAxis dataKey="label" stroke="#8892b0" tick={CHART_AXIS_TICK} tickLine={false} interval="preserveStartEnd" minTickGap={20} />
        <YAxis stroke="#8892b0" tick={CHART_AXIS_TICK} width={64} tickLine={false} tickFormatter={(v) => money(v)} allowDecimals={false} />
        <CartesianGrid strokeDasharray="3 3" stroke="#344155" opacity={0.3} />
        {/* Kept inside the chart's box on both axes, so a tall card for many brokers never runs
            off the side of a narrow screen. */}
        <Tooltip content={<CustomTooltip />} allowEscapeViewBox={{ x: false, y: false }} wrapperStyle={{ zIndex: 20 }} />
        <Legend wrapperStyle={{ fontSize: 12, color: '#9ca3af' }} />
        {series.map((s) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.colour}
            strokeWidth={2}
            dot={{ r: 3, fill: s.colour }}
            activeDot={{ r: 5, fill: s.colour }}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default VelocityTrendChart;
